/* KOSH product page helpers: info accordions, cm / inches tables, size guide dialog, variant-aware
   stock/notify block. No dependencies beyond Dawn's pubsub. Loaded on every page (kosh-head). */
(() => {
  if (window.__koshProduct) return;
  window.__koshProduct = true;

  // Custom analytics events for Shopify Customer Events (web pixels). Silently skipped where unavailable.
  const track = (name, data) => {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (err) {}
  };

  document.addEventListener(
    'click',
    (e) => {
      const guide = e.target.closest('[data-kosh-event="size_guide_open"]');
      if (guide) {
        track('size_guide_open', {
          product_id: Number(guide.dataset.koshProductId) || null,
          handle: guide.dataset.koshHandle || null,
        });
      }
      const wa = e.target.closest('a[href^="https://wa.me/"]');
      if (wa) track('whatsapp_click', { page: window.location.pathname, label: wa.textContent.trim() });
    },
    true
  );

  document.addEventListener(
    'submit',
    (e) => {
      const payload = e.target.querySelector?.('script[data-kosh-restock]');
      if (!payload) return;
      try {
        track('restock_request', JSON.parse(payload.textContent));
      } catch (err) {}
    },
    true
  );

  // Size guide dialog
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-kosh-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.koshDialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        dialog._koshOpener = opener;
      }
      return;
    }
    const closer = e.target.closest('[data-kosh-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    // Click on the backdrop closes
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('kosh-dialog')) e.target.close();
  });
  document.addEventListener(
    'close',
    (e) => {
      if (e.target instanceof HTMLDialogElement && e.target._koshOpener) e.target._koshOpener.focus();
    },
    true
  );

  // Swap the stock / notify block with the server-rendered HTML for the newly selected variant
  const attach = () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const { sectionId, html } = event.data || {};
      if (!sectionId || !html) return;
      const source = html.getElementById(`KoshVariantInfo-${sectionId}`);
      const target = document.getElementById(`KoshVariantInfo-${sectionId}`);
      if (source && target) target.innerHTML = source.innerHTML;

      // Dawn resets an enabled button to "Add to bag"; keep the server label (e.g. "Pre-order")
      const buttonSource = html.querySelector(`#ProductSubmitButton-${sectionId} > span`);
      const button = document.getElementById(`ProductSubmitButton-${sectionId}`);
      if (buttonSource && button && !button.hasAttribute('disabled')) {
        const label = button.querySelector(':scope > span');
        if (label) label.textContent = buttonSource.textContent.trim();
      }

      const stickySource = html.getElementById(`KoshStickyAtc-${sectionId}`);
      const sticky = document.getElementById(`KoshStickyAtc-${sectionId}`);
      if (stickySource && sticky) {
        sticky.innerHTML = stickySource.innerHTML;
        sticky.dataset.available = stickySource.dataset.available;
        sticky._koshUpdate?.();
      }
    });
  };

  // Sticky Add to bag (phones). Shown only when a purchasable size is selected, the main button is out of
  // view and no dialog/drawer is open. Moved to <body> so no transformed ancestor can break position: fixed.
  const mobile = window.matchMedia('(max-width: 749px)');
  const overlayOpen = () =>
    !!document.querySelector('dialog[open], cart-drawer.active, details[open] > .menu-drawer, .drawer.active') ||
    /\boverflow-hidden/.test(document.body.className);

  const setupSticky = (sticky) => {
    const sectionId = sticky.dataset.koshStickyAtc;
    const mainButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    if (!mainButton) return;
    document.querySelectorAll(`body > #KoshStickyAtc-${sectionId}`).forEach((old) => old !== sticky && old.remove());
    if (sticky.parentElement !== document.body) document.body.appendChild(sticky);

    let mainInView = true;
    const update = () => {
      const picker = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
      const idInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
      const selected = !picker?.dataset.koshAwaitingSize && !!idInput?.value;
      const show =
        mobile.matches && selected && sticky.dataset.available === 'true' && !mainInView && !overlayOpen();
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
    };
    sticky._koshUpdate = update;

    new IntersectionObserver((entries) => {
      mainInView = entries[0].isIntersecting;
      update();
    }).observe(mainButton);
    new MutationObserver((records) => {
      if (records.some((r) => !sticky.contains(r.target))) update();
    }).observe(document.body, {
      attributes: true,
      attributeFilter: ['open', 'class'],
      subtree: true,
    });
    mobile.addEventListener('change', update);
    document.addEventListener('change', (e) => e.target.closest?.('variant-selects') && update());
  };
  const initSticky = () => document.querySelectorAll('[data-kosh-sticky-atc]').forEach(setupSticky);
  document.addEventListener('shopify:section:load', initSticky);
  // No pre-selected size: on first load of a multi-variant product without ?variant=, clear the size
  // option and disable the buy button. Dawn's own fetch/URL update takes over after the first tap.
  const clearInitialSize = () => {
    if (new URLSearchParams(window.location.search).has('variant')) return;
    document.querySelectorAll('product-info[id^="MainProduct"] variant-selects[data-kosh-variant-count]').forEach((picker) => {
      if (Number(picker.dataset.koshVariantCount) < 2) return;
      const groups = Array.from(picker.querySelectorAll('fieldset'));
      const sizeGroups = groups.filter((g) => g.querySelector('input[data-option-name]')?.dataset.optionName.match(/size/i));
      const targets = sizeGroups.length ? sizeGroups : groups.length === 1 ? groups : [];
      if (!targets.length) return;

      targets.forEach((group) => {
        group.querySelectorAll('input:checked').forEach((input) => (input.checked = false));
        const shown = group.querySelector('[data-selected-value]');
        if (shown) shown.textContent = '';
      });

      const sectionId = picker.dataset.section;
      const idInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
      if (idInput) idInput.value = '';
      const button = document.getElementById(`ProductSubmitButton-${sectionId}`);
      if (button) {
        button.setAttribute('disabled', 'disabled');
        const label = button.querySelector('span');
        if (label) label.textContent = picker.dataset.koshSelectLabel;
      }
      const info = document.getElementById(`KoshVariantInfo-${sectionId}`);
      const stock = info?.querySelector('.kosh-stock');
      if (stock) stock.textContent = '';
      info?.querySelectorAll('.kosh-preorder, input[name^="properties["]').forEach((el) => el.remove());
      picker.dataset.koshAwaitingSize = 'true';
      picker.addEventListener('change', () => delete picker.dataset.koshAwaitingSize, { once: true });
    });
  };

  // Product info accordions: one open at a time (fallback for browsers without <details name>)
  document.addEventListener(
    'toggle',
    (e) => {
      const opened = e.target;
      if (!(opened instanceof HTMLDetailsElement) || !opened.open || !opened.classList.contains('kosh-acc')) return;
      opened
        .closest('[data-kosh-accordion]')
        ?.querySelectorAll(':scope > details.kosh-acc[open]')
        .forEach((other) => other !== opened && (other.open = false));
    },
    true
  );

  // Links such as "Details" and "Size guide" open their accordion and scroll to it
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-kosh-acc-open]');
    if (!link) return;
    const target = document.getElementById(link.dataset.koshAccOpen);
    if (!target) return;
    e.preventDefault();
    target.open = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    target.querySelector('summary')?.focus({ preventScroll: true });
  });

  // cm / inches: every measurement table on the page follows the last choice (remembered per browser)
  const formatUnit = (n) => String(Math.round(n * 10) / 10);
  const applyUnit = (unit) => {
    document.querySelectorAll('[data-kosh-table]').forEach((table) => {
      const base = table.dataset.koshBaseUnit || 'cm';
      table.querySelectorAll('[data-kosh-measure]').forEach((cell) => {
        if (cell.dataset.koshOrig === undefined) cell.dataset.koshOrig = cell.textContent;
        const orig = cell.dataset.koshOrig;
        cell.textContent =
          unit === base
            ? orig
            : orig.replace(/\d+(?:\.\d+)?/g, (n) => formatUnit(unit === 'in' ? Number(n) / 2.54 : Number(n) * 2.54));
      });
      table.querySelectorAll('[data-kosh-unit-label]').forEach((label) => (label.textContent = unit));
    });
    document.querySelectorAll('[data-kosh-units] [data-kosh-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.koshUnit === unit));
    });
  };
  let storedUnit = null;
  try {
    storedUnit = window.localStorage.getItem('kosh-unit');
  } catch (err) {}
  const initUnits = () => {
    const toggles = document.querySelectorAll('[data-kosh-units]');
    if (!toggles.length) return;
    toggles.forEach((toggle) => (toggle.hidden = false));
    applyUnit(storedUnit === 'in' ? 'in' : 'cm');
  };
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-kosh-units] [data-kosh-unit]');
    if (!button) return;
    storedUnit = button.dataset.koshUnit;
    try {
      window.localStorage.setItem('kosh-unit', storedUnit);
    } catch (err) {}
    applyUnit(storedUnit);
  });
  document.addEventListener('shopify:section:load', initUnits);

  // Product videos: play muted while on screen, pause when scrolled away. A visitor's Pause sticks.
  // With reduced motion they don't start on their own (the button reads "Play video").
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const videoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              const wrap = entry.target;
              wrap._koshVisible = entry.isIntersecting;
              const video = wrap.querySelector('video');
              if (!video) return;
              if (entry.isIntersecting && !wrap._koshUserPaused && !reduceMotion.matches) {
                const p = video.play();
                if (p && p.catch) p.catch(() => {});
              } else if (!entry.isIntersecting) video.pause();
            }),
          { threshold: 0.35 }
        )
      : null;
  const setVideoButton = (wrap) => {
    const video = wrap.querySelector('video');
    const button = wrap.querySelector('[data-kosh-video-toggle]');
    if (!video || !button) return;
    const playing = !video.paused;
    button.textContent = playing ? 'Pause video' : 'Play video';
    button.setAttribute('aria-pressed', String(!playing));
  };
  const initVideos = () => {
    document.querySelectorAll('[data-kosh-video]').forEach((wrap) => {
      if (wrap._koshVideo) return;
      const video = wrap.querySelector('video');
      if (!video) return;
      wrap._koshVideo = true;
      video.muted = true;
      video.setAttribute('playsinline', '');
      ['play', 'pause'].forEach((type) => video.addEventListener(type, () => setVideoButton(wrap)));
      setVideoButton(wrap);
      setSoundButton(wrap);
      if (videoObserver) videoObserver.observe(wrap);
      else if (!reduceMotion.matches) video.play().catch(() => {});
    });
  };
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-kosh-video-toggle]');
    if (!button) return;
    const wrap = button.closest('[data-kosh-video]');
    const video = wrap && wrap.querySelector('video');
    if (!video) return;
    if (video.paused) {
      wrap._koshUserPaused = false;
      video.play().catch(() => {});
    } else {
      wrap._koshUserPaused = true;
      video.pause();
    }
  });
  // Sound: off by default (muted autoplay). Turning it on for one video mutes every other one and makes sure it plays.
  const setSoundButton = (wrap) => {
    const video = wrap.querySelector('video');
    const button = wrap.querySelector('[data-kosh-video-sound]');
    if (!video || !button) return;
    const on = !video.muted;
    // The visible label ("Sound off" / "Sound on") is the accessible name, so voice control matches what people see
    button.setAttribute('aria-pressed', String(on));
    button.classList.toggle('is-on', on);
    const label = button.querySelector('[data-kosh-video-sound-label]');
    if (label) label.textContent = on ? 'Sound on' : 'Sound off';
  };
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-kosh-video-sound]');
    if (!button) return;
    const wrap = button.closest('[data-kosh-video]');
    const video = wrap && wrap.querySelector('video');
    if (!video) return;
    const turnOn = video.muted;
    if (turnOn) {
      document.querySelectorAll('[data-kosh-video] video').forEach((v) => {
        if (v !== video && !v.muted) {
          v.muted = true;
          setSoundButton(v.closest('[data-kosh-video]'));
        }
      });
      video.muted = false;
      video.volume = 1;
      wrap._koshUserPaused = false;
      if (video.paused) video.play().catch(() => {});
      track('product_video_sound_on', { page: window.location.pathname });
    } else {
      video.muted = true;
    }
    setSoundButton(wrap);
  });
  document.addEventListener('volumechange', (e) => {
    const wrap = e.target.closest && e.target.closest('[data-kosh-video]');
    if (wrap) setSoundButton(wrap);
  }, true);
  document.addEventListener('shopify:section:load', initVideos);

  const init = () => {
    initUnits();
    initVideos();
    clearInitialSize();
    attach();
    initSticky();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
